import React, { memo, useCallback, useState, useEffect, useMemo } from 'react';
import { Handle, Position, useReactFlow, useEdges, useNodes } from 'reactflow';
import CustomResizer from './CustomResizer';
import { Input } from 'antd';

import NodeLabel from './NodeLabel';
import { TYPE_COLORS, getHandleStyle } from './nodeStyles';
import { useNodeTitle } from './useNodeTitle';

const { TextArea } = Input;

const DynamicTextNode = ({ id, data, selected }) => {
    const { setNodes } = useReactFlow();
    const allEdges = useEdges();
    const allNodes = useNodes();
    const { onSave } = data;
    const onTitleChange = useNodeTitle(id, data);

    const [template, setTemplate] = useState(data.template || '');
    const [textFocused, setTextFocused] = useState(false);
    const [hovered, setHovered] = useState(false);

    // Handles are visible when node is selected or hovered
    const handlesVisible = selected || hovered;

    // Sync local state when external data changes
    useEffect(() => {
        if (!textFocused) {
            setTemplate(data.template || '');
        }
    }, [data.template, textFocused]);

    const updateStoreData = useCallback((key, value) => {
        setNodes((nodes) =>
            nodes.map((node) => {
                if (node.id === id) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            [key]: value,
                        },
                    };
                }
                return node;
            })
        );
    }, [id, setNodes]);

    const persistNodeData = useCallback((key, value) => {
        if (onSave) {
            onSave(id, { [key]: value });
        }
    }, [id, onSave]);

    // Track input order - stored in node data to persist across renders
    const inputOrder = data.inputOrder || [];

    // Get all connected text inputs - ordered by stored inputOrder
    const connectedInputs = useMemo(() => {
        // Find all edges targeting this node's input handle
        const incomingEdges = allEdges.filter(
            (e) => e.target === id && (e.targetHandle === 'left-input' || !e.targetHandle)
        );

        // Get text from each source node
        const inputMap = new Map();
        incomingEdges.forEach((edge) => {
            const sourceNode = allNodes.find((n) => n.id === edge.source);
            if (sourceNode && sourceNode.data) {
                const text = sourceNode.data.text || sourceNode.data.answer || '';
                inputMap.set(edge.source, {
                    nodeId: edge.source,
                    edgeId: edge.id,
                    nodeTitle: sourceNode.data.nodeTitle || sourceNode.data.label || 'Text',
                    text: text || '(empty)'
                });
            }
        });

        // Build ordered list based on stored inputOrder, then append any new connections
        const orderedInputs = [];
        const seenNodeIds = new Set();

        // First, add inputs in their stored order
        inputOrder.forEach((nodeId) => {
            if (inputMap.has(nodeId)) {
                orderedInputs.push(inputMap.get(nodeId));
                seenNodeIds.add(nodeId);
            }
        });

        // Then, append any new connections not yet in inputOrder
        inputMap.forEach((input, nodeId) => {
            if (!seenNodeIds.has(nodeId)) {
                orderedInputs.push(input);
            }
        });

        return orderedInputs;
    }, [allEdges, allNodes, id, inputOrder]);

    // Update stored inputOrder when connections change (only when edges change, not on every render)
    const connectedNodeIds = useMemo(() =>
        connectedInputs.map(i => i.nodeId).join(','),
        [connectedInputs]
    );

    useEffect(() => {
        const currentNodeIds = connectedInputs.map(i => i.nodeId);
        const storedOrder = data.inputOrder || [];

        // Only update if the actual set of connected nodes changed
        const storedSet = new Set(storedOrder);
        const currentSet = new Set(currentNodeIds);

        const hasNewConnections = currentNodeIds.some(id => !storedSet.has(id));
        const hasRemovedConnections = storedOrder.some(id => !currentSet.has(id));

        if ((hasNewConnections || hasRemovedConnections) && currentNodeIds.length > 0) {
            updateStoreData('inputOrder', currentNodeIds);
            persistNodeData('inputOrder', currentNodeIds);
        }
    }, [connectedNodeIds]); // Only run when the connected node IDs actually change

    // Build combined output by replacing placeholders
    const combinedOutput = useMemo(() => {
        let output = template;
        connectedInputs.forEach((input, idx) => {
            const num = idx + 1;
            output = output.replace(new RegExp(`\\{${num}\\}`, 'g'), input.text);
        });
        return output;
    }, [template, connectedInputs]);

    // Update the output text in node data for downstream connections
    useEffect(() => {
        if (data.text !== combinedOutput) {
            updateStoreData('text', combinedOutput);
        }
    }, [combinedOutput, data.text, updateStoreData]);

    const onTemplateChange = useCallback((evt) => {
        const val = evt.target.value;
        setTemplate(val);
        updateStoreData('template', val);
    }, [updateStoreData]);

    const onBlur = useCallback(() => {
        setTextFocused(false);
        persistNodeData('template', template);
    }, [template, persistNodeData]);

    const onFocus = useCallback(() => {
        setTextFocused(true);
    }, []);

    return (
        <div
            style={{
                background: 'transparent',
                padding: 20,
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <CustomResizer
                isVisible={selected}
                minWidth={200}
                minHeight={200}
                onResizeEnd={(event, params) => {
                    const { width, height, x, y } = params;
                    if (data.onResize) {
                        data.onResize(id, { width, height, x, y });
                    }
                }}
            />
            <NodeLabel
                title={data.nodeTitle || 'Dynamic Text'}
                iconType={data.iconType || 'merge-cells'}
                color={data.nodeColor || '#13c2c2'}
                onTitleChange={onTitleChange}
            />

            {/* Single Input Handle - full left side */}
            <Handle
                type="target"
                position={Position.Left}
                id="left-input"
                style={getHandleStyle(TYPE_COLORS.text, 'left', handlesVisible)}
            />

            <div style={{
                background: '#161b22',
                borderRadius: 8,
                border: '1px solid #30363d',
                boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                display: 'flex',
                gap: 10,
                padding: 12,
                flex: 1,
                flexDirection: 'column',
                height: '100%',
            }}>
                {/* Connected Inputs List */}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {connectedInputs.length === 0 ? (
                        <div style={{
                            padding: '4px 8px',
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 500,
                            background: '#21262d',
                            color: '#8b949e',
                            border: '1px solid #30363d',
                        }}>
                            Connect text nodes →
                        </div>
                    ) : (
                        connectedInputs.map((input, idx) => (
                            <div
                                key={input.nodeId}
                                style={{
                                    padding: '4px 8px',
                                    borderRadius: 4,
                                    fontSize: 10,
                                    fontWeight: 500,
                                    background: '#238636',
                                    color: '#fff',
                                    border: '1px solid #238636',
                                }}
                            >
                                {`{${idx + 1}}`}: {input.text.length > 25 ? input.text.substring(0, 25) + '...' : input.text}
                            </div>
                        ))
                    )}
                </div>

                <div className="nodrag" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0 }}>
                    {/* Template Input */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                        <span style={{ fontSize: 10, fontWeight: 'bold', color: '#8b949e', textTransform: 'uppercase' }}>
                            Template (use {'{1}'}, {'{2}'}, etc.)
                        </span>
                        <TextArea
                            value={template}
                            onChange={onTemplateChange}
                            onBlur={onBlur}
                            onFocus={onFocus}
                            placeholder="I want to build a new: {1}. Make it a great shot of {2}."
                            style={{
                                background: '#0d1117',
                                border: '1px solid #30363d',
                                color: '#c9d1d9',
                                fontSize: 14,
                                resize: 'none',
                                flex: 1,
                                fontFamily: 'Inter, sans-serif',
                            }}
                        />
                    </div>

                    {/* Combined Output Preview */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 'bold', color: '#8b949e', textTransform: 'uppercase' }}>
                            Combined Output
                        </span>
                        <div style={{
                            background: '#0d1117',
                            border: '1px solid #30363d',
                            borderRadius: 4,
                            padding: 10,
                            color: '#58a6ff',
                            fontSize: 13,
                            fontFamily: 'Inter, sans-serif',
                            minHeight: 60,
                            maxHeight: 120,
                            overflowY: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                        }}>
                            {combinedOutput || <span style={{ color: '#484f58', fontStyle: 'italic' }}>Output will appear here...</span>}
                        </div>
                    </div>
                </div>
            </div>

            {/* Output Handle */}
            <Handle
                type="source"
                position={Position.Right}
                id="right-output"
                style={getHandleStyle(TYPE_COLORS.text, 'right', handlesVisible)}
            />
        </div>
    );
};

export default memo(DynamicTextNode);
